import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Select, Input, Button, message, Alert, Descriptions, Tag, Space, Tooltip, Typography, Divider, Checkbox } from "antd";
import { SwapOutlined, CheckOutlined, WarningFilled, InfoCircleOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { UTCTimePicker } from "../../../../components/DateTimeInput";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { useAuth } from "../../../../hooks/useAuth";
import { createErrorHandler } from "../../../../utils/errorHandler";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS, DIVERSION_CATEGORIES, buildDiversionReason, parseDiversionReason } from "../constants";

dayjs.extend(utc);
dayjs.extend(duration);

const { Text } = Typography;

/**
 * 从页面级机场主数据缓存中取备降机场的最小过站时间（分钟）。
 *
 * 数据源优先级（设计文档 §4.4）：
 *   1. AirportInfo.turnaround_time（window.cachedAirports）
 *   2. 前序航班 min_ground_time_minutes（备降场景不适用，此处不取）
 *   3. 手动输入
 *
 * @param {string} airportCode 备降机场 IATA 代码
 * @returns {number|null} 最小过站分钟数；缓存缺失或机场不存在时返回 null（UI 降级为手动输入）
 */
const getAirportTurnaroundMinutes = (airportCode) => {
    if (!airportCode) return null;
    const cached = window.cachedAirports;
    if (!cached || !Array.isArray(cached)) return null;
    const airport = cached.find((a) => a.iata_code === airportCode);
    const minutes = airport?.turnaround_time;
    const parsed = minutes != null ? parseInt(minutes, 10) : NaN;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * 构建 IATA 备降分类下拉选项，并追加"自定义（不加分类前缀）"项。
 * 按位置/原因分组展示（Select 用 optgroup）。
 */
const buildCategoryOptions = () => {
    const groups = { POSITION: "按位置分类", CAUSE: "按原因分类" };
    return Object.keys(groups).map((groupKey) => ({
        label: groups[groupKey],
        title: groups[groupKey],
        options: [
            ...DIVERSION_CATEGORIES.filter((c) => c.group === groupKey).map((c) => ({
                value: c.code,
                label: c.label,
            })),
        ],
    }));
};

/**
 * 备降处理表单 Modal（设计文档 §7.1）
 *
 * 完整备降操作流程：录入备降机场 c / ATA@c / 备降分类 / 续飞段 STD，
 * 预览将生成的衍生航段 a-c 与 c-b，提交后由后端 DiversionService 执行原子性结构变更。
 *
 * 提交使用专用 `PROCESS_DIVERSION` 变更命令（§8.2），而非 UPDATE_FLIGHT_INFO。
 *
 * @param {boolean} props.open 是否可见
 * @param {Function} props.onClose 关闭回调
 * @param {Object} props.flight 原航班（a-b）对象
 * @param {Function} props.onSuccess 备降成功回调，签名 (originalFlightId, responseData) => void
 */
const DiversionModal = ({ open, onClose, flight, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [airportOptions, setAirportOptions] = useState([]);
    const { user } = useAuth();

    const handleDiversionError = createErrorHandler("记录备降", { showNotification: true, duration: 6 });

    // 监听表单字段变化，用于预览与 STD 联动计算
    const diversionAirport = Form.useWatch("diversion_airport", form);
    const divertedAt = Form.useWatch("diverted_at", form);
    const recoveryStdWatch = Form.useWatch("recovery_std", form);
    const recoveryFlightNumberSame = Form.useWatch("recovery_flight_number_same", form);
    const recoveryFlightNumberWatch = Form.useWatch("recovery_flight_number", form);

    const categoryOptions = useMemo(() => buildCategoryOptions(), []);

    // 原航班出发机场（a）与目的地（b）
    const departureAirport = flight?.departure_airport;
    const originalArrival = flight?.arrival_airport;
    const flightNumber = flight?.flight_number;
    const aircraftReg = flight?.aircraft_reg;
    const atdValue = flight?.atd;
    const useOriginalRecoveryFlightNumber = recoveryFlightNumberSame !== false;
    const recoveryFlightNumberInput = typeof recoveryFlightNumberWatch === "string" ? recoveryFlightNumberWatch.trim() : "";
    const recoveryFlightNumberPreview = useOriginalRecoveryFlightNumber || !recoveryFlightNumberInput ? flightNumber : recoveryFlightNumberInput;

    // 前置条件：原航班必须已起飞（§4.1）
    const hasAtd = !!atdValue && dayjs.utc(atdValue).isValid();

    // 备降机场 c 的最小过站时间（分钟）
    const turnaroundMinutes = useMemo(() => getAirportTurnaroundMinutes(diversionAirport), [diversionAirport]);

    // 是否具备自动计算 c-b STD 的条件（§4.4）
    const canAutoComputeStd = !!divertedAt && divertedAt.isValid() && turnaroundMinutes != null;

    // 自动计算 recovery_std（ATA@c + turnaround_time），用户可手动覆盖
    useEffect(() => {
        if (!open) return;
        if (canAutoComputeStd && divertedAt && turnaroundMinutes != null) {
            const computed = divertedAt.utc().add(turnaroundMinutes, "minute");
            form.setFieldValue("recovery_std", computed);
        }
        // 注意：丧失自动计算条件时不主动清空，保留用户已输入值，避免误删
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [divertedAt, turnaroundMinutes, canAutoComputeStd, open]);

    // Modal 打开时初始化表单
    useEffect(() => {
        if (open && flight) {
            form.resetFields();
            // 若原航班已有备降字段（二次打开/续填场景），回填以支持补填 ATA@c
            const existingReason = flight.diversion_reason || "";
            const parsed = parseDiversionReason(existingReason);
            form.setFieldsValue({
                diversion_airport: flight.diversion_airport || undefined,
                diverted_at: flight.diverted_at ? dayjs.utc(flight.diverted_at) : null,
                diversion_category: parsed.code || undefined,
                diversion_description: parsed.description || "",
                recovery_std: null,
                recovery_flight_number_same: true,
                recovery_flight_number: flight.flight_number || "",
            });
        }
    }, [open, flight, form]);

    // 加载机场主数据缓存为下拉选项（与 FlightInfoEditor 一致的模式）
    useEffect(() => {
        if (open && window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
            setAirportOptions(
                window.cachedAirports.map((airport) => ({
                    value: airport.iata_code,
                    label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                })),
            );
        }
    }, [open]);

    // 衍生段 a-c 状态动态推断（§3.2 / D11）
    const diversionLegStatus = useMemo(() => {
        if (divertedAt && divertedAt.isValid()) return FLIGHT_STATUS.COMPLETED;
        if (hasAtd) return FLIGHT_STATUS.DEPARTED; // 有原航班 ATD、无 ATA@c → 飞行中
        return FLIGHT_STATUS.SCHEDULED;
    }, [divertedAt, hasAtd]);

    // 前置条件缺失提示
    const prerequisitesError = useMemo(() => {
        if (!hasAtd) {
            return "该航班尚未起飞（缺少实际起飞时间 ATD）。按航空业范式，未起飞不属于备降，请改用改航或取消后重排计划。";
        }
        return null;
    }, [hasAtd]);

    // STD 自动计算降级提示（§4.4）
    const stdComputationHint = useMemo(() => {
        if (!diversionAirport) return null;
        if (!divertedAt || !divertedAt.isValid()) {
            return {
                type: "warning",
                text: "未填写备降机场实际落地时间（飞机在飞往备降机场途中），无法自动计算续飞航段 STD，请手动输入续飞航段起飞时间。飞机落地后可补填。",
            };
        }
        if (turnaroundMinutes == null) {
            return {
                type: "warning",
                text: `备降机场 ${diversionAirport} 未配置最小过站时间，无法自动计算 STD，请手动输入续飞航段的起飞时间。`,
            };
        }
        return {
            type: "success",
            text: `已根据 ${diversionAirport} 最小过站时间 ${turnaroundMinutes} 分钟自动预填续飞航段 STD，如需调整可直接修改。`,
        };
    }, [diversionAirport, divertedAt, turnaroundMinutes]);

    // 机场重复校验提示
    const airportConflict = useMemo(() => {
        if (!diversionAirport) return null;
        if (diversionAirport === originalArrival) return "备降机场不能与原目的地机场相同（已抵达目的地无需备降）";
        return null;
    }, [diversionAirport, departureAirport, originalArrival]);

    // 表单提交
    const handleSubmit = async () => {
        try {
            if (!user || !user.id) {
                message.error("用户未登录或身份验证失效，请重新登录");
                return;
            }
            if (prerequisitesError) {
                message.error(prerequisitesError);
                return;
            }

            const values = await form.validateFields();

            // 额外业务校验：recovery_std 必填（§4.4 不允许无 STD 生成 c-b）
            if (!values.recovery_std || !values.recovery_std.isValid()) {
                message.error("续飞航段起飞时间（STD）为必填项，无法省略");
                return;
            }

            // 时间合理性：c-b STD 不应早于 ATA@c（落地后才能起飞）
            if (values.diverted_at && values.diverted_at.isValid()) {
                if (values.recovery_std.isBefore(values.diverted_at)) {
                    message.warning("续飞航段 STD 早于备降机场实际落地时间，请确认时间是否正确");
                }
            }

            setLoading(true);

            const diversionReason = buildDiversionReason(values.diversion_category, values.diversion_description);
            const submittedRecoveryFlightNumber = typeof values.recovery_flight_number === "string" ? values.recovery_flight_number.trim() : "";
            const recoveryFlightNumberOverride =
                values.recovery_flight_number_same === false && submittedRecoveryFlightNumber && submittedRecoveryFlightNumber !== flightNumber
                    ? submittedRecoveryFlightNumber
                    : null;

            // 构造 PROCESS_DIVERSION payload（字段对齐数据模型 §3.1）
            const diversionPayload = {
                diversion_airport: values.diversion_airport,
                diverted_at: values.diverted_at && values.diverted_at.isValid() ? values.diverted_at.utc().format() : null,
                diversion_reason: diversionReason,
                recovery_std: values.recovery_std.utc().format(),
                recovery_flight_number: recoveryFlightNumberOverride,
            };

            const response = await flightOperationFlightService.processDiversion(
                flight.id,
                diversionPayload,
                { operator: user.id, triggerSource: "DIVERSION_MODAL" },
                { _silentError: true },
            );

            if (response.data?.success) {
                const responseData = response.data.data;
                // 批量响应中检查失败项
                if (responseData?.failed_items?.length > 0) {
                    const failedItem = responseData.failed_items.find((item) => item.flight_id === flight.id);
                    if (failedItem) {
                        throw new Error(failedItem.error || "记录备降失败");
                    }
                }
                message.success(`航班 ${flightNumber || flight.id} 备降已记录，已生成备降航段与续飞航段`);
                form.resetFields();
                onClose();
                if (onSuccess) {
                    onSuccess(flight.id, responseData);
                }
            } else {
                throw new Error(response.data?.message || "记录备降失败");
            }
        } catch (error) {
            handleDiversionError(error);
        } finally {
            setLoading(false);
        }
    };

    // 渲染衍生航段预览（§7.1 G 节点）
    const renderDerivedLegsPreview = () => {
        const showStd = recoveryStdWatch && recoveryStdWatch.isValid();

        return (
            <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6, padding: 12 }}>
                <Space style={{ marginBottom: 8 }}>
                    <ThunderboltOutlined style={{ color: "#722ed1" }} />
                    <Text strong>航段预览（提交后将自动创建以下航段）</Text>
                </Space>
                <Descriptions size="small" column={1} bordered labelStyle={{ width: 90, background: "#fff" }}>
                    <Descriptions.Item label="① 备降航段">
                        <Space wrap size={4}>
                            <Tag color="blue">{flightNumber || "-"}</Tag>
                            <Text>
                                {departureAirport || "—"} <SwapOutlined style={{ fontSize: 11, color: "#999" }} />{" "}
                                <Text strong style={{ color: "#722ed1" }}>
                                    {diversionAirport}
                                </Text>
                            </Text>
                            <Tag color={FLIGHT_STATUS_COLORS[diversionLegStatus]} style={{ fontSize: 11 }}>
                                {FLIGHT_STATUS_LABELS[diversionLegStatus]}
                            </Tag>
                            {aircraftReg && <Tag>{aircraftReg}</Tag>}
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                继承原航班号与机号
                            </Text>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="② 续飞航段">
                        <Space wrap size={4}>
                            <Tag color="blue">{recoveryFlightNumberPreview || "-"}</Tag>
                            <Text>
                                <Text strong style={{ color: "#722ed1" }}>
                                    {diversionAirport}
                                </Text>{" "}
                                <SwapOutlined style={{ fontSize: 11, color: "#999" }} /> {originalArrival || "—"}
                            </Text>
                            <Tag style={{ fontSize: 11, color: "#108ee9" }}>
                                {FLIGHT_STATUS_LABELS[FLIGHT_STATUS.SCHEDULED]}
                            </Tag>
                            {aircraftReg && <Tag>{aircraftReg}</Tag>}
                            <Text type={showStd ? "success" : "danger"} style={{ fontSize: 11 }}>
                                STD: {showStd ? recoveryStdWatch.utc().format("YYYY-MM-DD HH:mm") + " UTC" : "待填写"}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                航班号可覆盖，机号继承
                            </Text>
                        </Space>
                    </Descriptions.Item>
                </Descriptions>
                <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
                    原航班将标记为备降（DIVERTED 状态），保留原始计划意图；关联事务将迁移至备降航段并标记为待复核。
                </Text>
            </div>
        );
    };

    return (
        <Modal
            title={
                <div>
                    <SwapOutlined style={{ color: "#722ed1", marginRight: 8 }} />
                    记录备降
                    <span style={{ color: "#888", marginLeft: "10px", fontSize: "14px", fontWeight: "normal" }}>
                        {flightNumber} ({departureAirport} → {originalArrival})
                    </span>
                </div>
            }
            open={open}
            onCancel={onClose}
            width={760}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    取消
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!!prerequisitesError}
                    icon={<CheckOutlined />}
                >
                    确认记录备降
                </Button>,
            ]}
            destroyOnHidden
            maskClosable={false}
            centered
        >
            {/* 前置条件拦截（§4.1） */}
            {prerequisitesError && (
                <Alert
                    message="不满足备降前置条件"
                    description={prerequisitesError}
                    type="error"
                    showIcon
                    icon={<WarningFilled />}
                    style={{ marginBottom: 16 }}
                />
            )}

            {!prerequisitesError && (
                <>
                    <Alert
                        message="备降将自动完成航段结构变更"
                        description="原航班将标记为备降，系统自动生成「备降航段」与「续飞航段」，关联事务迁移至备降航段并标记待复核。"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form form={form} layout="vertical" autoComplete="off">
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
                            {/* 备降机场 c（必填） */}
                            <Form.Item
                                name="diversion_airport"
                                label="备降机场"
                                rules={[{ required: true, message: "请选择备降机场" }]}
                                style={{ width: "calc(50% - 8px)" }}
                                extra={
                                    airportConflict ? (
                                        <Text type="danger" style={{ fontSize: 12 }}>
                                            {airportConflict}
                                        </Text>
                                    ) : null
                                }
                            >
                                <Select
                                    showSearch
                                    placeholder="选择备降机场"
                                    options={airportOptions}
                                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                />
                            </Form.Item>

                            {/* ATA@c（可选） */}
                            <Form.Item
                                name="diverted_at"
                                label={
                                    <Tooltip title="备降机场实际落地时间。飞机在飞往备降机场途中时可留空（飞行中记录备降），待落地后补填。">
                                        实际落地时间 (ATA) <InfoCircleOutlined style={{ fontSize: 11, color: "#999" }} />
                                    </Tooltip>
                                }
                                style={{ width: "calc(50% - 8px)" }}
                                extra={
                                    !divertedAt || !divertedAt.isValid() ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            可选 · 留空表示飞行中记录（备降航段状态为「飞行中」）
                                        </Text>
                                    ) : null
                                }
                            >
                                <UTCTimePicker showDate format="HH:mm" style={{ width: "100%" }} minuteStep={1} allowClear />
                            </Form.Item>

                            {/* 备降分类（建议填） */}
                            <Form.Item
                                name="diversion_category"
                                label={
                                    <Tooltip title="选择备降的分类类型，便于后续按分类统计和分析">
                                        备降分类 <InfoCircleOutlined style={{ fontSize: 11, color: "#999" }} />
                                    </Tooltip>
                                }
                                style={{ width: "calc(50% - 8px)" }}
                                extra={<Text type="secondary" style={{ fontSize: 12 }}>建议填写，便于后续按分类统计</Text>}
                            >
                                <Select
                                    placeholder="选择备降分类（可留空以自定义）"
                                    options={categoryOptions}
                                    allowClear
                                    onChange={() => {
                                        /* 分类变化由提交时统一拼接，无需即时处理 */
                                    }}
                                />
                            </Form.Item>

                            {/* 续飞段 c-b STD（必填） */}
                            <Form.Item
                                name="recovery_std"
                                label="续飞航段起飞时间 (STD)"
                                rules={[{ required: true, message: "续飞航段 STD 为必填项（不允许无 STD 生成待飞航段）" }]}
                                style={{ width: "calc(50% - 8px)" }}
                                extra={
                                    stdComputationHint ? (
                                        <Text type={stdComputationHint.type === "success" ? "success" : "warning"} style={{ fontSize: 12 }}>
                                            {stdComputationHint.text}
                                        </Text>
                                    ) : null
                                }
                            >
                                <UTCTimePicker showDate format="HH:mm" style={{ width: "100%" }} minuteStep={1} allowClear />
                            </Form.Item>

                            <Form.Item
                                label="续飞航段航班号"
                                style={{ width: "calc(50% - 8px)" }}
                                extra={
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        勾选时继承原航班号；取消勾选后可为续飞航段输入新航班号
                                    </Text>
                                }
                            >
                                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                    <Form.Item name="recovery_flight_number_same" valuePropName="checked" noStyle>
                                        <Checkbox
                                            onChange={(event) => {
                                                if (event.target.checked) {
                                                    form.setFieldValue("recovery_flight_number", flightNumber || "");
                                                }
                                            }}
                                        >
                                            与原航班相同
                                        </Checkbox>
                                    </Form.Item>
                                    <Form.Item name="recovery_flight_number" noStyle>
                                        <Input
                                            allowClear
                                            disabled={useOriginalRecoveryFlightNumber}
                                            maxLength={12}
                                            placeholder={`默认继承原航班号 ${flightNumber || "-"}，如需修改请输入`}
                                        />
                                    </Form.Item>
                                </Space>
                            </Form.Item>

                            {/* 备降原因描述（自由文本） */}
                            <Form.Item
                                name="diversion_description"
                                label="备降原因描述"
                                style={{ width: "100%" }}
                            >
                                <Input.TextArea rows={2} placeholder="如：目的地雷暴，机组决定备降" maxLength={180} showCount />
                            </Form.Item>
                        </div>

                        <Divider style={{ margin: "8px 0 16px" }} />

                        {/* 衍生航段预览 */}
                        {renderDerivedLegsPreview()}
                    </Form>
                </>
            )}
        </Modal>
    );
};

export default DiversionModal;
